import { computed, signal } from "@preact/signals";

export interface SelectableCollection {
  length: number;
  totalCount?: number;
  pluck?(attr: string): string[];
  get?(id: string): any;
  getWordIDs?(): string[];
  getWord?(id: string): any;
  getSelectionQuery?(): Record<string, any>;
  performAction?(
    actionID: string,
    selection: SelectionStore
  ): void | Promise<void>;
  searchQuery?: {
    get?(key: string): any;
    toJSON?(): any;
    value?: Record<string, any>;
  };
  [key: string]: any;
}

export default class SelectionStore {
  readonly itemIDs = signal<string[]>([]);
  readonly wholeCollection = signal(false);
  readonly wholePage = signal(false);
  readonly count;

  collection: SelectableCollection;

  constructor(collection: SelectableCollection) {
    this.collection = collection;
    this.count = computed(() => {
      if (this.wholeCollection.value) {
        return this.collection.totalCount ?? 0;
      }
      return this.itemIDs.value.length;
    });

    // Keep itemIDs in sync when stores are removed from the collection
    if (collection.on) {
      collection.on("remove", (model: any) => {
        const id = model.id || model.get("_id");
        if (id && this.itemIDs.value.includes(id)) {
          this.removeItem(id);
        }
      });
    }
  }

  addItem(itemID: string) {
    this.addItems([itemID]);
  }

  addItems(itemIDs: string[]) {
    const current = this.itemIDs.value;
    const combined = [...new Set([...current, ...itemIDs])];
    this.itemIDs.value = combined;
    const pageIDs = this.collection.getWordIDs?.();
    let wholePage: boolean;
    if (pageIDs !== undefined) {
      wholePage =
        pageIDs.length > 0 && pageIDs.every((id) => combined.includes(id));
    } else {
      wholePage = combined.length === this.collection.length;
    }
    if (wholePage) {
      this.wholePage.value = true;
    }
  }

  removeItems(itemIDs: string[]) {
    const toRemove = new Set(itemIDs);
    const current = this.itemIDs.value;
    const removed = current.some((id) => toRemove.has(id));
    if (removed) {
      this.wholeCollection.value = false;
      this.wholePage.value = false;
      this.itemIDs.value = current.filter((id) => !toRemove.has(id));
    }
  }

  removeItem(itemID: string) {
    this.wholeCollection.value = false;
    this.wholePage.value = false;
    const current = this.itemIDs.value;
    const index = current.indexOf(itemID);
    if (index !== -1) {
      const next = [...current];
      next.splice(index, 1);
      this.itemIDs.value = next;
    }
  }

  selectWholePage() {
    this.wholePage.value = true;
    const ids = this.collection.getWordIDs?.() ?? this.collection.pluck!("_id");
    this.itemIDs.value = ids;
  }

  selectWholeCollection() {
    this.wholeCollection.value = true;
    const ids = this.collection.getWordIDs?.() ?? this.collection.pluck!("_id");
    this.itemIDs.value = ids;
  }

  clearSelection() {
    this.wholeCollection.value = false;
    this.wholePage.value = false;
    this.itemIDs.value = [];
  }

  getPageCount() {
    return this.collection.length;
  }

  getTotalCount() {
    return this.collection.totalCount ?? 0;
  }

  performAction(action: any) {
    let filteredSelection: SelectionStore;

    if (this.wholeCollection.value || this.wholePage.value) {
      filteredSelection = this;
    } else {
      filteredSelection = this.getFilteredSelection((item: any) => {
        return action.appliesToItem(item);
      });
    }

    if (this.collection.performAction) {
      return this.collection.performAction(action.id, filteredSelection);
    } else if (typeof this.collection[action.id] === "function") {
      return this.collection[action.id].bind(this.collection)(
        filteredSelection
      );
    } else {
      console.error("action not available: ", action);
    }
  }

  getFilteredSelection(filterFunction: (item: any) => boolean): SelectionStore {
    const filteredIDs: string[] = [];
    for (const item of this.getSelectedItems()) {
      if (filterFunction(item)) {
        filteredIDs.push(item.id ?? item.get("_id"));
      }
    }
    const filtered = new SelectionStore(this.collection);
    filtered.itemIDs.value = filteredIDs;
    return filtered;
  }

  getQuery() {
    if (this.wholeCollection.value) {
      const queryJSON =
        this.collection.getSelectionQuery?.() ??
        this.collection.searchQuery!.toJSON?.() ??
        this.collection.searchQuery!.value!;
      const {
        contexts: _contexts,
        from: _from,
        limit: _limit,
        sortBy: _sortBy,
        ...rest
      } = queryJSON;
      return {
        wholeCollection: true,
        ...rest,
      };
    } else {
      return {
        itemIDs: this.itemIDs.value.join("|"),
      };
    }
  }

  getSelectedItems() {
    return this.itemIDs.value.map(
      (itemID) =>
        this.collection.getWord?.(itemID) ?? this.collection.get!(itemID)
    );
  }

  toJSON() {
    return {
      itemIDs: this.itemIDs.value,
      wholeCollection: this.wholeCollection.value,
      wholePage: this.wholePage.value,
      count: this.count.value,
    };
  }
}
